import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  DeltaChip — percentage change indicator with directional arrow    */
/* ------------------------------------------------------------------ */

export interface DeltaChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Percentage change value (e.g. 12.5 or -8.3). */
  value: number;
  /** Optional label (e.g. "vs last year"). */
  label?: string;
  /** Size variant. */
  size?: "sm" | "md";
  /** Invert color logic (higher = bad, e.g. for cancellation rate). */
  invertColor?: boolean;
}

const css = /* css */ `
  .sf-delta-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-weight: var(--sf-weight-medium);
    line-height: 1;
    padding: 2px 7px;
    border-radius: var(--sf-radius-sm);
    white-space: nowrap;
  }

  .sf-delta-chip--sm {
    font-size: var(--sf-text-xs);
    padding: 1px 5px;
  }

  .sf-delta-chip--md {
    font-size: var(--sf-text-sm);
    padding: 2px 8px;
  }

  .sf-delta-chip--positive {
    color: var(--sf-success);
    background: var(--sf-success-light);
  }

  .sf-delta-chip--negative {
    color: var(--sf-error);
    background: var(--sf-error-light);
  }

  .sf-delta-chip--neutral {
    color: var(--sf-text-muted);
    background: var(--sf-bg-inset);
  }

  .sf-delta-chip__label {
    color: inherit;
    opacity: 0.7;
    margin-left: 2px;
  }
`;

function ArrowUp() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
      <path d="M5 2L8 6H2L5 2Z" />
    </svg>
  );
}

function ArrowDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
      <path d="M5 8L2 4H8L5 8Z" />
    </svg>
  );
}

const DeltaChip = React.forwardRef<HTMLSpanElement, DeltaChipProps>(
  ({ value, label, size = "sm", invertColor = false, className, ...rest }, ref) => {
    injectStyles("sf-delta-chip", css);

    const isPositive = value > 0;
    const isNegative = value < 0;
    const isNeutral = value === 0;

    let variant: "positive" | "negative" | "neutral";
    if (isNeutral) {
      variant = "neutral";
    } else if (invertColor) {
      variant = isPositive ? "negative" : "positive";
    } else {
      variant = isPositive ? "positive" : "negative";
    }

    const classes = [
      "sf-delta-chip",
      `sf-delta-chip--${size}`,
      `sf-delta-chip--${variant}`,
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <span ref={ref} className={classes} {...rest}>
        {isPositive && <ArrowUp />}
        {isNegative && <ArrowDown />}
        {isPositive ? "+" : ""}
        {value.toFixed(1)}%
        {label && <span className="sf-delta-chip__label">{label}</span>}
      </span>
    );
  },
);

DeltaChip.displayName = "DeltaChip";
export { DeltaChip };
